/* eslint-disable */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';

import Master from '../Master';
import IziToast from '../IziToast';
import Button from '../Buttons';
import PayModal from '../PayModal';
import ManualPaymentDetails from '../PayModal/ManualPaymentDetails';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'arrears', label: 'Arrears' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'over_paid', label: 'Over paid' },
];

const STATUS_PILL = {
  paid: { color: '#27ae60', bg: '#eafaf1', label: 'Paid' },
  arrears: { color: '#f39c12', bg: '#fff4e0', label: 'Arrears' },
  unpaid: { color: '#c0392b', bg: '#fdecea', label: 'Unpaid' },
  over_paid: { color: '#2980b9', bg: '#e8f4fb', label: 'Over paid' },
};

const currency = 'KES';
// Cap at 2dp — bare toLocaleString() with no options lets binary
// floating-point noise from upstream arithmetic leak straight into the UI.
const moneyOpts = { maximumFractionDigits: 2 };

const formatMoney = (n) => {
  const v = Number(n) || 0;
  return v < 0
    ? `(${currency} ${Math.abs(v).toLocaleString(undefined, moneyOpts)})`
    : `${currency} ${v.toLocaleString(undefined, moneyOpts)}`;
};

const ServiceCharge = ({ user }) => {
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'application/json' },
  };

  // Filters
  const [period, setPeriod] = useState(moment.utc().format('YYYY-MM'));
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [owners, setOwners] = useState([]);
  const [userId, setUserId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Data
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Manual "Pay" + read-only viewer modal state ─────────────────────
  const [payRow, setPayRow] = useState(null);
  const [manualViewRow, setManualViewRow] = useState(null);

  // Send invoice in-flight state, keyed by row id (unit_owner_id + unit_number)
  const [sendingInvoiceKey, setSendingInvoiceKey] = useState(null);
  // ── Send-Invoice channel-picker modal state ────────────────────────
  // Lets the operator pick Email / SMS / WhatsApp per send instead of
  // unconditionally fanning out to all three. Default: Email + SMS on.
  const [sendInvoiceRow, setSendInvoiceRow] = useState(null);
  const [sendChEmail, setSendChEmail] = useState(true);
  const [sendChSms, setSendChSms] = useState(true);
  const [sendChWhatsapp, setSendChWhatsapp] = useState(false);

  // ── Remark modal state ─────────────────────────────────────────────
  const [remarkRow, setRemarkRow] = useState(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);

  // Search (client-side filtering of rows)
  const [search, setSearch] = useState('');

  // ── Load filter options ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const propUrl = user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}properties?status=active&take=1000&order={"name":"ASC"}`
          : `${process.env.REACT_APP_API_URL}properties/byuser?status=active&user_id=${user.id}`;
        const { data: { response } } = await axios.get(propUrl, requestConfig);
        const list = Array.isArray(response) ? response : [response];
        setProperties(list);

        const { data: { response: ownersList } } = await axios.get(
          `${process.env.REACT_APP_API_URL}users?type=unit_owner&status=active`,
          requestConfig,
        );
        setOwners(ownersList || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ── Load rows whenever filters change ───────────────────────────────────
  useEffect(() => {
    loadRows();
  }, [period, propertyId, userId, statusFilter]);

  const loadRows = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('period', period);
      if (propertyId) params.set('property_id', propertyId);
      if (userId) params.set('user_id', userId);
      if (statusFilter) params.set('status', statusFilter);
      // Note: unlike Invoices/WaterBill, getServiceCharges already computes
      // payment_channel/manual fields unconditionally (it iterates
      // owner_payments per row anyway) — no include_payment_info flag needed.
      const { data: { response } } = await axios.get(
        `${process.env.REACT_APP_API_URL}servicecharges?${params.toString()}`,
        requestConfig,
      );
      setRows((response && response.rows) || []);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to load service charges');
      setRows([]);
    }
    setIsLoading(false);
  };

  // ── Client-side search ──────────────────────────────────────────────────
  const visibleRows = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.property_name || '').toLowerCase().includes(q)
      || (r.owner_name || '').toLowerCase().includes(q)
      || (r.unit_number || '').toLowerCase().includes(q)
    );
  });

  // ── Pending total (unpaid balance across visible rows) ──────────────────
  const total = visibleRows.reduce((s, r) => s + (Number(r.balance) || 0), 0);

  // ── Manual "Pay" modal ──────────────────────────────────────────────
  const openPayModal = (row) => setPayRow(row);
  const closePayModal = () => setPayRow(null);
  const onPaySuccess = () => {
    setPayRow(null);
    loadRows();
  };
  const buildPayFormData = ({
    row, amount, mode, transactionDetails, file,
  }) => {
    const fd = new FormData();
    fd.append('user_id', row.user_id);
    fd.append('property_id', row.property_id);
    if (row.unit_owner_id) fd.append('unit_owner_id', row.unit_owner_id);
    fd.append('unit_number', row.unit_number);
    fd.append('period', period);
    fd.append('amount', amount);
    fd.append('mode', mode);
    fd.append('transaction_details', transactionDetails || '');
    if (file) fd.append('proof', file);
    return fd;
  };

  // ── Send invoice ────────────────────────────────────────────────────────
  // Open the channel-picker modal. Defaults to Email + SMS on (legacy
  // behavior); user opts WhatsApp in per send.
  const openSendInvoiceModal = (row) => {
    setSendInvoiceRow(row);
    setSendChEmail(true);
    setSendChSms(true);
    setSendChWhatsapp(false);
  };

  const closeSendInvoiceModal = () => {
    if (sendingInvoiceKey) return;
    setSendInvoiceRow(null);
  };

  const submitSendInvoice = async () => {
    const row = sendInvoiceRow;
    if (!row) return;
    if (!sendChEmail && !sendChSms && !sendChWhatsapp) {
      IziToast('Pick at least one channel');
      return;
    }
    const key = `${row.unit_owner_id}-${row.unit_number}`;
    setSendingInvoiceKey(key);
    const picked = [];
    if (sendChEmail) picked.push('email');
    if (sendChSms) picked.push('SMS');
    if (sendChWhatsapp) picked.push('WhatsApp');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}servicecharges/send-invoice`,
        {
          user_id: row.user_id,
          property_id: row.property_id,
          unit_number: row.unit_number,
          period,
          send_email: sendChEmail,
          send_sms: sendChSms,
          send_whatsapp: sendChWhatsapp,
        },
        requestConfig,
      );
      IziToast(`Invoice sent to ${row.owner_name || 'owner'} via ${picked.join(', ')}`);
      setSendInvoiceRow(null);
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to send invoice');
    }
    setSendingInvoiceKey(null);
  };

  // ── Remark modal ────────────────────────────────────────────────────────
  const openRemarkModal = (row) => {
    setRemarkRow(row);
    setRemarkDraft(row.remark || '');
  };

  const closeRemarkModal = () => {
    setRemarkRow(null);
    setRemarkDraft('');
  };

  const saveRemark = async () => {
    if (!remarkRow) return;
    setRemarkSaving(true);
    try {
      const { data: { response } } = await axios.put(
        `${process.env.REACT_APP_API_URL}servicecharges/remark`,
        {
          property_id: remarkRow.property_id,
          unit_number: remarkRow.unit_number,
          period,
          remark: remarkDraft,
        },
        requestConfig,
      );
      IziToast(`Remark saved for ${remarkRow.unit_number}`);
      const savedValue = (response && response.remark) || null;
      // Patch the visible row so the indicator/button styling update
      // immediately without waiting for a full reload.
      setRows((prev) => prev.map((r) => (
        r.property_id === remarkRow.property_id
          && r.unit_number === remarkRow.unit_number
          ? { ...r, remark: savedValue }
          : r
      )));
      closeRemarkModal();
    } catch (e) {
      IziToast(e.response?.data?.error || 'Failed to save remark');
    }
    setRemarkSaving(false);
  };

  // ── CSV export ──────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (visibleRows.length === 0) {
      IziToast('No rows to export');
      return;
    }
    const headers = [
      'No.', 'Property', 'House No.', 'Unit Owner', 'Rate', 'Previous Balance',
      'Current Balance', 'Total Amount', 'Amount Paid', 'Balance', 'Status',
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csvRows = visibleRows.map((r, i) => [
      i + 1,
      r.property_name || '',
      r.unit_number || '',
      r.owner_name || '',
      r.rate || 0,
      r.previous_balance || 0,
      r.current_balance || 0,
      r.arrears || 0,
      r.amount_paid || 0,
      r.balance || 0,
      STATUS_PILL[r.status] ? STATUS_PILL[r.status].label : r.status,
    ].map(esc).join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `service-charge-${period}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const periodLabel = moment.utc(`${period}-01`).format('MMMM YYYY');

  return (
    <Master redirectLink="servicecharge" title="Finance" source="Service Charge">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Service Charge for the Month: <span style={{ color: '#c0392b' }}>{periodLabel}</span></h3>
        <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: '8px 16px', minWidth: 180, textAlign: 'right' }}>
          <div style={{ color: '#999', fontSize: 12, fontWeight: 'bold' }}>Pending Total</div>
          <div style={{ color: '#c0392b', fontSize: 22, fontWeight: 'bold' }}>
            {currency} {total.toLocaleString(undefined, moneyOpts)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row" style={{ marginBottom: 16 }}>
        <div className="col-md-2">
          <label className="control-label">Period</label>
          <input
            type="month"
            className="form-control"
            value={period}
            onChange={(e) => setPeriod(e.target.value || moment.utc().format('YYYY-MM'))}
          />
        </div>
        <div className="col-md-3">
          <label className="control-label">Select Property</label>
          <select
            className="form-control"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="control-label">Select Unit Owner</label>
          <select
            className="form-control"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">All unit owners</option>
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {`${o.first_name} ${o.last_name}`}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="control-label">Payment Status</label>
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2" style={{ paddingTop: 22 }}>
          <Button caption="Refresh" cssClass="btn-success" onClick={loadRows} type="button" isBlack={false} />
        </div>
      </div>

      {/* Table actions row */}
      <h4 style={{ marginTop: 18 }}>Service Charge Payment</h4>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <button type="button" className="btn btn-default btn-sm" onClick={exportCSV}>
            <i className="fa fa-file-text-o" /> CSV
          </button>
          <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
            Showing <strong>{visibleRows.length}</strong> of {rows.length}
          </span>
        </div>
        <div>
          <label style={{ fontSize: 13, marginRight: 8 }}>Search:</label>
          <input
            type="text"
            className="form-control"
            style={{ display: 'inline-block', width: 220 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>No.</th>
              <th>Property</th>
              <th>House No.</th>
              <th>Unit Owner</th>
              <th>Rate</th>
              <th>Previous Balance</th>
              <th>Current Balance</th>
              <th>Amount Due</th>
              <th>Amount Paid</th>
              <th>Balance</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan="12" style={{ textAlign: 'center', padding: 20, color: '#888' }}>Loading...</td></tr>
            )}
            {!isLoading && visibleRows.length === 0 && (
              <tr><td colSpan="12" style={{ textAlign: 'center', padding: 20, color: '#888' }}>No service charge records for this filter.</td></tr>
            )}
            {!isLoading && visibleRows.map((r, idx) => {
              const pill = STATUS_PILL[r.status] || { color: '#555', bg: '#eee', label: r.status };
              return (
                <tr key={`${r.unit_owner_id}-${r.unit_number}`}>
                  <td>{idx + 1}</td>
                  <td>{r.property_name || '-'}</td>
                  <td>{r.unit_number}</td>
                  <td style={{ color: r.owner_name ? 'inherit' : '#bbb' }}>{r.owner_name || '—'}</td>
                  <td>{formatMoney(r.rate)}</td>
                  <td>{formatMoney(r.previous_balance)}</td>
                  <td>{formatMoney(r.current_balance)}</td>
                  <td>{formatMoney(r.arrears)}</td>
                  <td>{formatMoney(r.amount_paid)}</td>
                  <td style={{ color: r.balance > 0 ? '#c0392b' : r.balance < 0 ? '#2980b9' : '#27ae60', fontWeight: 'bold' }}>
                    {formatMoney(r.balance)}
                  </td>
                  <td>
                    <span
                      style={{
                        background: pill.bg,
                        color: pill.color,
                        padding: '3px 10px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 'bold',
                      }}
                    >
                      {pill.label}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => openSendInvoiceModal(r)}
                      disabled={sendingInvoiceKey === `${r.unit_owner_id}-${r.unit_number}`}
                      style={{ marginRight: 4 }}
                      title="Send invoice to the unit owner (pick channels)"
                    >
                      <i className="fa fa-paper-plane" style={{ marginRight: 4 }} />
                      {sendingInvoiceKey === `${r.unit_owner_id}-${r.unit_number}` ? 'Sending...' : 'Send Invoice'}
                    </button>
                    {r.balance > 0 && (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => openPayModal(r)}
                        style={{ marginRight: 4 }}
                        title="Record a manual payment for this unit"
                      >
                        <i className="fa fa-money" style={{ marginRight: 4 }} />
                        Pay
                      </button>
                    )}
                    {r.status === 'paid' && r.payment_channel === 'manual' && (
                      <button
                        type="button"
                        className="btn btn-info btn-sm"
                        onClick={() => setManualViewRow(r)}
                        style={{ marginRight: 4 }}
                        title="View manual payment details"
                      >
                        <i className="fa fa-eye" style={{ marginRight: 4 }} />
                        Manual
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn btn-sm ${r.remark ? 'btn-info' : 'btn-default'}`}
                      onClick={() => openRemarkModal(r)}
                      style={{ marginRight: 4 }}
                      title={r.remark ? 'View / edit remark' : 'Add a remark for this row'}
                    >
                      <i
                        className={`fa ${r.remark ? 'fa-sticky-note' : 'fa-sticky-note-o'}`}
                        style={{ marginRight: 4 }}
                      />
                      Remark
                    </button>
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      title="View PDF invoice"
                      onClick={() => {
                        const codePart = String(r.property_code || r.property_name || 'SC')
                          .toUpperCase()
                          .replace(/\s+/g, '');
                        const unitPart = String(r.unit_number || '').toUpperCase();
                        const periodPart = String(r.period || period).replace('-', '');
                        const ref = `SC-${codePart}-${unitPart}-${periodPart}`;
                        const base = (process.env.REACT_APP_API_URL || '').replace(/\/?$/, '');
                        window.open(`${base}/public/servicecharge.pdf?ref=${ref}&kind=invoice`, '_blank');
                      }}
                    >
                      <i className="fa fa-file-pdf-o" style={{ marginRight: 4 }} />
                      PDF
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PayModal
        row={payRow}
        title="Pay Service Charge"
        subtitle={payRow ? `${payRow.owner_name || 'Unit owner'} — ${payRow.property_name || ''} Unit ${payRow.unit_number} (${period})` : ''}
        maxAmount={payRow ? Number(payRow.balance) : null}
        endpoint={`${process.env.REACT_APP_API_URL}servicecharges/payments`}
        requestConfig={requestConfig}
        buildFormData={buildPayFormData}
        onClose={closePayModal}
        onSuccess={onPaySuccess}
      />
      <ManualPaymentDetails
        row={manualViewRow}
        amount={manualViewRow ? manualViewRow.amount_paid : null}
        onClose={() => setManualViewRow(null)}
      />

      {/* ── Send Invoice channel-picker modal ────────────────────────── */}
      {sendInvoiceRow && (
        <div
          onClick={closeSendInvoiceModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              width: 'min(420px, 92vw)',
              borderRadius: 6,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee' }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                {`Send service charge to ${sendInvoiceRow.owner_name || 'owner'}`}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                {`Unit ${sendInvoiceRow.unit_number} · ${period} · pick the channels.`}
              </div>
            </div>
            <div style={{ padding: '14px 20px' }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendChEmail}
                  onChange={(e) => setSendChEmail(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send Email
              </label>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendChSms}
                  onChange={(e) => setSendChSms(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send SMS
              </label>
              <label style={{ display: 'block', fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendChWhatsapp}
                  onChange={(e) => setSendChWhatsapp(e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Send WhatsApp
              </label>
            </div>
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #eee',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={closeSendInvoiceModal}
                disabled={sendingInvoiceKey !== null}
                style={{
                  border: '1px solid #ccc',
                  background: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: sendingInvoiceKey !== null ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitSendInvoice}
                disabled={sendingInvoiceKey !== null}
                style={{
                  border: '1px solid var(--red_color, #d63835)',
                  background: 'var(--red_color, #d63835)',
                  color: '#fff',
                  padding: '6px 16px',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: sendingInvoiceKey !== null ? 'not-allowed' : 'pointer',
                }}
              >
                {sendingInvoiceKey !== null ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remark modal ───────────────────────────────────────────────── */}
      {remarkRow && (
        <div
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) closeRemarkModal(); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 22,
              width: 'min(520px, 90vw)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 12,
            }}
            >
              <div>
                <h4 style={{ margin: 0, color: '#2c3e50' }}>
                  Remark — Unit {remarkRow.unit_number}
                </h4>
                <div style={{ fontSize: 12, color: '#7c8896', marginTop: 4 }}>
                  {remarkRow.property_name} · {period}
                  {remarkRow.owner_name ? ` · ${remarkRow.owner_name}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={closeRemarkModal}
                style={{
                  border: 'none', background: 'none', fontSize: 22,
                  color: '#888', cursor: 'pointer', lineHeight: 1,
                }}
                title="Close"
              >
                ×
              </button>
            </div>
            <textarea
              className="form-control"
              rows={5}
              placeholder="e.g. Owner promised to pay by end of month; sent reminder via WhatsApp."
              value={remarkDraft}
              onChange={(e) => setRemarkDraft(e.target.value)}
              style={{ resize: 'vertical' }}
            />
            <div style={{
              marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}
            >
              <button
                type="button"
                className="btn btn-default"
                onClick={closeRemarkModal}
                disabled={remarkSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={saveRemark}
                disabled={remarkSaving}
              >
                {remarkSaving ? 'Saving…' : 'Save Remark'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Master>
  );
};

const mapStateToProps = (state) => state;
ServiceCharge.propTypes = { user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) };
ServiceCharge.defaultProps = { user: {} };
export default connect(mapStateToProps)(ServiceCharge);
